export type ShortcutKey = 'mod' | 'shift' | 'alt' | 'ctrl' | (string & {})

export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false

  if (navigator.platform) {
    return /Mac|iPhone|iPad|iPod/.test(navigator.platform)
  }

  return /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)
}

function matchesKey(event: KeyboardEvent, key: string): boolean {
  const normalized = key.toLowerCase()

  if (normalized.length === 1 && /^[a-z0-9]$/.test(normalized)) {
    return (
      event.key.toLowerCase() === normalized ||
      event.code === `Key${normalized.toUpperCase()}` ||
      event.code === `Digit${normalized}`
    )
  }

  return event.key.toLowerCase() === normalized
}

function hasModKey(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey
}

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false

  const tagName = target.tagName.toLowerCase()
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true
  }

  return target.isContentEditable
}

export function matchesShortcut(
  event: KeyboardEvent,
  keys: ShortcutKey[],
): boolean {
  const normalizedKeys = keys.map((key) => key.toLowerCase())
  const mainKeys = normalizedKeys.filter(
    (key) => !['mod', 'shift', 'alt', 'ctrl'].includes(key),
  )

  if (mainKeys.length !== 1) return false

  const mainKey = mainKeys[0]
  if (!mainKey || !matchesKey(event, mainKey)) return false

  const wantsMod = normalizedKeys.includes('mod')
  const wantsCtrl = normalizedKeys.includes('ctrl')
  const wantsShift = normalizedKeys.includes('shift')
  const wantsAlt = normalizedKeys.includes('alt')

  if (wantsShift !== event.shiftKey) return false
  if (wantsAlt !== event.altKey) return false

  if (wantsMod) {
    if (!hasModKey(event)) return false
  } else if (wantsCtrl) {
    if (!event.ctrlKey) return false
  } else if (hasModKey(event)) {
    return false
  }

  if (wantsCtrl && !event.ctrlKey) return false

  return true
}

export type FormattedShortcutPart = {
  type: 'key'
  value: string
}

export function formatShortcut(keys: ShortcutKey[]): FormattedShortcutPart[] {
  const normalizedKeys = keys.map((key) => key.toLowerCase())
  const parts: FormattedShortcutPart[] = []
  const isMac = isMacPlatform()

  if (normalizedKeys.includes('mod')) {
    parts.push({ type: 'key', value: isMac ? '⌘' : 'Ctrl' })
  }

  if (normalizedKeys.includes('ctrl')) {
    parts.push({ type: 'key', value: 'Ctrl' })
  }

  if (normalizedKeys.includes('alt')) {
    parts.push({ type: 'key', value: isMac ? '⌥' : 'Alt' })
  }

  if (normalizedKeys.includes('shift')) {
    parts.push({ type: 'key', value: isMac ? '⇧' : 'Shift' })
  }

  for (const key of normalizedKeys) {
    if (['mod', 'shift', 'alt', 'ctrl'].includes(key)) continue
    parts.push({
      type: 'key',
      value: key.length === 1 ? key.toUpperCase() : key,
    })
  }

  return parts
}
